import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
  import router from "@/router";
  import store from "@/store";
  import {
    changePrimaryColor,
    resetPrimaryColor,
  } from "@/helpers/customer/changePrimaryColor";
  import { POSITION, useToast } from "vue-toastification";
  import { EstateInterface } from "@/types/EstateInterface";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import {
    HouseDesignInterface,
    HouseLotsBuilderPagination,
    HouseWithLotsAndBuilder,
  } from "@/types/HouseDesignInterface";
  import BuilderLogo from "@/components/builder/budget-houses/house-details/BuilderLogo.vue";
  import HouseNameAndFeatures from "@/components/builder/budget-houses/house-details/HouseNameAndFeatures.vue";
  import HouseAndFloorplanImage from "@/components/builder/budget-houses/house-details/HouseAndFloorplanImage.vue";
  import HouseBrowseInteriors from "@/components/builder/budget-houses/house-details/HouseBrowseInteriors.vue";
  import HouseAndLotDetails from "@/components/builder/budget-houses/house-details/HouseAndLotDetails.vue";
  import { BuilderQuoteWithDetails } from "@/types/BuilderQuote";
  import FilterHouses from "@/components/builder/budget-houses/FilterHouses.vue";
  import ListHouses from "@/components/builder/budget-houses/ListHouses.vue";
  import { LotInterface } from "@/types/LotInterface";
  import { formatPrice } from "@/helpers/transformation/price";
  import { BuilderInterface } from "@/types/BuilderInterface";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import BuilderHouseBorrowHeaderMobile from "@/components/builder/budget-houses/house-details/BuilderHouseBorrowHeaderMobile.vue";
  import FloorplanAndInclusionsMobile from "@/components/builder/budget-houses/house-details/FloorplanAndInclusionsMobile.vue";
  import BuilderFiltersHousesModal from "@/components/ui/modals/BuilderFiltersHousesModal.vue";
  import HouseQuestion from "@/components/builder/budget-houses/house-details/HouseQuestion.vue";
  import { RouteParamsGeneric } from "vue-router";
  import { FacadeInterface } from "@/types/FacadeInterface";

  const smallLayoutWidth = 990;
  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderHouseBorrowCalculator',
  setup(__props, { expose: __expose }) {
  __expose();

  const estateId = ref<string>(
    router.currentRoute.value.params.estateId as string
  );
  const houseId = ref<string>(
    router.currentRoute.value.params.houseId as string
  );
  const quoteId = ref<string>(
    router.currentRoute.value.params.quoteId as string
  );
  const loanAmount = ref<string>(
    router.currentRoute.value.params.loanAmount as string
  );
  const estateInfo = ref<EstateInterface>();
  const houseInfo = ref<HouseDesignInterface>();
  const quoteInfo = ref<BuilderQuoteWithDetails>();
  const builderInfo = ref<BuilderInterface>();
  const toast = useToast();
  const loading = ref(true);
  const showFloorplan = ref(false);
  const showInterior = ref(false);
  const houses = ref<HouseWithLotsAndBuilder[]>();
  const lotSelected = ref<LotInterface>();
  const deleteLot = ref<boolean>(false);
  const imageSelected = ref<string | null>(null);
  const isOpenedModalFiltersHouses = ref(false);
  const isModalFiltersHousesHidden = ref(true);
  const nextHousesBatch = ref<HouseLotsBuilderPagination>();
  const hasToLoadNextBatch = ref(false);
  const selectedFacade = ref<FacadeInterface | null>(null);

  const { isDesktop } = useIsDesktop(smallLayoutWidth);

  const resultForFilter = computed(() => ({
    LoanAmountString: formatPrice(loanAmount.value),
  }));

  const onUpdateSelectedFacade = (facade: FacadeInterface) => {
    selectedFacade.value = facade;
  };

  const onUpdateHousesPagination = (housesPage: HouseLotsBuilderPagination) => {
    hasToLoadNextBatch.value = false;
    nextHousesBatch.value = housesPage;
  };

  const onLoadNextBatch = () => {
    hasToLoadNextBatch.value = true;
  };

  const getHouseById = () => {
    if (houseId.value) {
      store
        .dispatch("builder/getHouseById", houseId.value.toString())
        .then((res) => {
          if (res.data) {
            houseInfo.value = res.data;
          }
        })
        .then(() => {
          getBuilderById();
        })
        .catch(() => {
          toast.error("Something went wrong", {
            position: POSITION.TOP_LEFT,
          });
        })
        .finally(() => {
          loading.value = false;
        });
    }
  };

  const getQuoteWithData = () => {
    store
      .dispatch("builder/getQuoteWithDetails", quoteId.value)
      .then((res) => {
        quoteInfo.value = res.data;
        estateInfo.value = quoteInfo.value?.estate;
        lotSelected.value = quoteInfo.value?.lot;
        houseInfo.value = quoteInfo.value?.house;
        builderInfo.value = quoteInfo.value?.builder;
      })
      .catch(() => {
        toast.error("Something went wrong", {
          position: POSITION.TOP_LEFT,
        });
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const getLot = async (id: string) => {
    try {
      const res = await store.dispatch("lot/getOpenLot", id);

      if (res.data) {
        lotSelected.value = res.data;
      }
    } catch (error) {
      toast.error("An error occurred while getting the lot");
    }
  };

  const getBuilderById = () => {
    if (houseInfo.value) {
      store
        .dispatch("builder/getBuilderById", houseInfo.value.builderId)
        .then((res) => {
          builderInfo.value = res.data;
        })
        .catch((err) => {
          console.error(
            "Error fetching builder by id in BuilderHouseBorrowCalculator.vue",
            err
          );
          toast.error("Error fetching builder by id", {
            position: POSITION.TOP_LEFT,
          });
        });
    }
  };

  const onViewCalculator = () => {
    router.push({
      name: "BuilderBorrowCalculator",
      params: {
        estateId: estateId.value,
        quoteId: quoteId.value,
      },
    });
  };

  const housesByFilter = (housesData: HouseWithLotsAndBuilder[]) => {
    houses.value = housesData;
  };

  const openModalFilter = () => {
    isOpenedModalFiltersHouses.value = true;
    isModalFiltersHousesHidden.value = false;
  };

  const refreshLot = async (lotId?: string) => {
    if (lotId) {
      deleteLot.value = false;
      await getLot(lotId);
    }
  };

  const refreshHouse = async (lotId?: string) => {
    loading.value = true;
    refreshHouseLocalStorage();
    await refreshLot(lotId);
  };

  const refreshHouseLocalStorage = () => {
    const storedDeleteLot = localStorage.getItem("deleteLot");

    if (storedDeleteLot) {
      localStorage.removeItem("deleteLot");
    }

    const newStoredDeleteLot = localStorage.getItem("deleteLotStorage");

    if (newStoredDeleteLot === "true") {
      deleteLot.value = true;
    }

    const savedFilterFields = localStorage.getItem("filterFields");

    if (savedFilterFields) {
      store.commit(
        "borrowCalculator/updateFilter",
        JSON.parse(savedFilterFields)
      );
    }
  };

  onMounted(() => {
    getQuoteWithData();
  });

  onUnmounted(() => {
    resetPrimaryColor();
    localStorage.removeItem("deleteLotStorage");
    localStorage.removeItem("filterFields");
  });

  const updateHouseIfNewParams = (
    newParams: RouteParamsGeneric,
    oldParams: RouteParamsGeneric | undefined
  ) => {
    if (newParams?.houseId !== oldParams?.houseId) {
      houseId.value = newParams.houseId as string;
      getHouseById();
    }
  };

  const updateLoanAmountIfNewParams = (
    newParams: RouteParamsGeneric,
    oldParams: RouteParamsGeneric | undefined
  ) => {
    if (newParams.loanAmount !== oldParams?.loanAmount) {
      loanAmount.value = newParams.loanAmount as string;
    }
  };

  watch(
    () => router.currentRoute.value.params,
    (newParams, oldParams) => {
      if (!quoteInfo.value) return;

      updateHouseIfNewParams(newParams, oldParams);
      updateLoanAmountIfNewParams(newParams, oldParams);
    },
    { immediate: true }
  );

  watch(
    () => estateInfo.value,
    () => {
      if (estateInfo.value) {
        changePrimaryColor(estateInfo.value.iconColor);
      }
    }
  );

const __returned__ = { estateId, houseId, quoteId, loanAmount, estateInfo, houseInfo, quoteInfo, builderInfo, toast, loading, showFloorplan, showInterior, houses, lotSelected, deleteLot, imageSelected, isOpenedModalFiltersHouses, isModalFiltersHousesHidden, nextHousesBatch, hasToLoadNextBatch, selectedFacade, smallLayoutWidth, isDesktop, resultForFilter, onUpdateSelectedFacade, onUpdateHousesPagination, onLoadNextBatch, getHouseById, getQuoteWithData, getLot, getBuilderById, onViewCalculator, housesByFilter, openModalFilter, refreshLot, refreshHouse, refreshHouseLocalStorage, updateHouseIfNewParams, updateLoanAmountIfNewParams, computed, onMounted, onUnmounted, ref, watch, get router() { return router }, get store() { return store }, get changePrimaryColor() { return changePrimaryColor }, get resetPrimaryColor() { return resetPrimaryColor }, get POSITION() { return POSITION }, get useToast() { return useToast }, get EstateInterface() { return EstateInterface }, DropDown, get HouseDesignInterface() { return HouseDesignInterface }, get HouseLotsBuilderPagination() { return HouseLotsBuilderPagination }, get HouseWithLotsAndBuilder() { return HouseWithLotsAndBuilder }, BuilderLogo, HouseNameAndFeatures, HouseAndFloorplanImage, HouseBrowseInteriors, HouseAndLotDetails, get BuilderQuoteWithDetails() { return BuilderQuoteWithDetails }, FilterHouses, ListHouses, get LotInterface() { return LotInterface }, get formatPrice() { return formatPrice }, get BuilderInterface() { return BuilderInterface }, get useIsDesktop() { return useIsDesktop }, BuilderHouseBorrowHeaderMobile, FloorplanAndInclusionsMobile, BuilderFiltersHousesModal, HouseQuestion, get RouteParamsGeneric() { return RouteParamsGeneric }, get FacadeInterface() { return FacadeInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})